import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import StepCard from 'shared/components/StepCard';
import FormInput from 'shared/components/forms/FormInput';
import FormMultiSelect from 'shared/components/forms/FormMultiSelect';
import FormRadioGroup from 'shared/components/forms/FormRadioGroup';
import FormSelect from 'shared/components/forms/FormSelect';
import PhoneVerificationPanel from 'features/onboarding/components/PhoneVerificationPanel';
import {
  isPhoneVerified,
  useOnboarding,
} from 'features/onboarding/context/OnboardingContext';
import { useRegisterStepNav } from 'features/onboarding/context/StepNavContext';
import { useOnboardingOptions } from 'features/onboarding/hooks/useOnboardingOptions';
import {
  buildCityOptions,
  buildStoreTypeOptions,
  filterStoreTypesByCategory,
} from 'features/onboarding/utils/cityLabel';
import { stepBusinessSchema } from 'features/onboarding/schemas';
import { enrichPotentialLead } from 'features/onboarding/api/savePotentialLead';
import { ltrIsolate } from 'shared/lib/bidi';
import {
  BUSINESS_CATEGORIES,
  DELIVERY_COMPANIES,
  KOSHER_CERTIFICATIONS,
  SENSITIVITIES,
  type BusinessCategory,
  type DeliveryCompany,
  type KosherCertification,
  type Sensitivity,
} from 'features/onboarding/types/onboarding.types';

interface LocalState {
  businessCategory: BusinessCategory | '';
  storeType: string;
  storeTypeOther: string;
  location: string;
  sensitivities: Sensitivity[];
  kosherCertifications: KosherCertification[];
  deliveryCompanies: DeliveryCompany[];
  otherDeliveryCompanies: string;
  businessPhoneNumber: string;
  email: string;
}

const isOtherStoreType = (value: string): boolean => {
  const v = value.trim().toLowerCase();
  return v === 'other' || value.trim() === 'אחר';
};

/**
 * The partner is never asked "are you kosher?" / "do you work with other
 * delivery apps?" — picking (or not picking) anything in the matching
 * multi-select answers both. Pipedrive and the statistics dashboard still read
 * the booleans, so they are derived here instead of being collected.
 */
const derivedFlags = (
  state: Pick<LocalState, 'kosherCertifications' | 'deliveryCompanies'>,
): { isKosher: boolean; worksWithOtherDeliveryCompanies: boolean } => ({
  isKosher: state.kosherCertifications.length > 0,
  worksWithOtherDeliveryCompanies: state.deliveryCompanies.length > 0,
});

/**
 * Keeps 3 leading and 3 trailing digits, so anything shorter than 7 characters
 * has nothing left to star out and is shown as typed.
 */
const maskPhone = (phone: string): string =>
  phone.length < 7
    ? phone
    : `${phone.slice(0, 3)}${'*'.repeat(phone.length - 6)}${phone.slice(-3)}`;

/**
 * Step 2 — everything about the business itself: what it is, how it operates,
 * and how to reach it. The map-derived address stays in context from step 1
 * (not editable here); the partner picks their HAAT city from a select.
 *
 * This step also hosts the SMS gate: until the mobile number from step 1
 * answers its challenge, the code entry takes the place of the business form.
 */
const Step3Business: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data, updateSection, setMetadata, goNext, goBack, registerStepGuard } =
    useOnboarding();
  const { data: options } = useOnboardingOptions();

  const verified = isPhoneVerified(data);

  const cityOptions = useMemo(
    () => buildCityOptions(options?.areas, i18n.language),
    [options, i18n.language],
  );

  const [local, setLocal] = useState<LocalState>({
    businessCategory: data.businessInfo.businessCategory,
    storeType: data.businessInfo.storeType,
    storeTypeOther: data.businessQuestions.storeTypeOther ?? '',
    location: data.businessInfo.location,
    sensitivities: data.businessQuestions.sensitivities ?? [],
    kosherCertifications: data.businessQuestions.kosherCertifications,
    deliveryCompanies: data.businessQuestions.deliveryCompanies,
    otherDeliveryCompanies: data.businessQuestions.otherDeliveryCompanies,
    businessPhoneNumber: data.businessInfo.businessPhoneNumber,
    email: data.businessInfo.email,
  });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  // Once city options load, drop a raw Google city that is not in the HAAT
  // list so the select does not show a phantom value the partner cannot keep.
  useEffect(() => {
    if (!cityOptions.length) return;
    setLocal((s) => {
      if (!s.location) return s;
      if (cityOptions.some((o) => o.value === s.location)) return s;
      return { ...s, location: '' };
    });
  }, [cityOptions]);

  const storeTypeOptions = useMemo(
    () =>
      buildStoreTypeOptions(
        filterStoreTypesByCategory(options?.storeTypes, local.businessCategory),
        i18n.language,
      ),
    [options, local.businessCategory, i18n.language],
  );

  const categoryOptions = BUSINESS_CATEGORIES.map((c) => ({
    value: c,
    label: t(`businessCategory.options.${c}`) as string,
  }));

  const setField = <K extends keyof LocalState>(key: K, value: LocalState[K]) =>
    setLocal((s) => ({ ...s, [key]: value }));

  const setCompanies = (next: string[]) => {
    setLocal((s) => {
      const typed = next as DeliveryCompany[];
      const nextOther = typed.includes('other') ? s.otherDeliveryCompanies : '';
      return { ...s, deliveryCompanies: typed, otherDeliveryCompanies: nextOther };
    });
  };

  const validate = (): boolean => {
    const result = stepBusinessSchema.safeParse({
      ...local,
      address: data.businessInfo.address,
    });
    if (result.success) {
      setErrors({});
      return true;
    }
    const next: Record<string, string | undefined> = {};
    result.error.issues.forEach((issue) => {
      const key = issue.path[0];
      if (typeof key === 'string' && !next[key]) next[key] = issue.message;
    });
    setErrors(next);
    return false;
  };

  const persist = () => {
    const resolvedStoreType = isOtherStoreType(local.storeType)
      ? local.storeTypeOther.trim()
      : local.storeType;
    const { isKosher, worksWithOtherDeliveryCompanies } = derivedFlags(local);

    updateSection('businessInfo', {
      businessCategory: local.businessCategory,
      storeType: resolvedStoreType as never,
      location: local.location,
      businessPhoneNumber: local.businessPhoneNumber,
      email: local.email,
    });
    updateSection('businessQuestions', {
      isKosher,
      kosherCertifications: local.kosherCertifications,
      sensitivities: local.sensitivities,
      storeTypeOther: local.storeTypeOther,
      worksWithOtherDeliveryCompanies,
      deliveryCompanies: local.deliveryCompanies,
      otherDeliveryCompanies: local.otherDeliveryCompanies,
    });
  };

  const handleNext = async () => {
    if (!validate()) return;
    persist();

    await enrichPotentialLead({
      commercialName: data.businessInfo.commercialName,
      ownerName: data.businessInfo.ownerName,
      phoneNumber: data.businessInfo.phoneNumber,
      address: data.businessInfo.address,
      location: local.location,
      latitude: data.businessInfo.latitude,
      longitude: data.businessInfo.longitude,
      businessCategory: local.businessCategory || undefined,
      storeType: isOtherStoreType(local.storeType)
        ? local.storeTypeOther.trim()
        : local.storeType,
      ...derivedFlags(local),
      kosherCertifications: local.kosherCertifications,
      sensitivities: local.sensitivities,
      deliveryCompanies: local.deliveryCompanies,
      otherDeliveryCompanies: local.otherDeliveryCompanies,
      businessPhoneNumber: local.businessPhoneNumber || undefined,
      email: local.email,
    });

    goNext();
  };

  // Verifying doesn't navigate: the flag flips and the business form replaces
  // the code entry in place.
  const handleVerified = (token: string) => {
    setMetadata({
      phoneVerified: true,
      verifiedPhone: data.businessInfo.phoneNumber,
      phoneVerificationToken: token,
      otpChallengeId: undefined,
      otpSentTo: undefined,
    });
  };

  const handleChallenge = (challengeId: string, sentTo: string) => {
    setMetadata({ otpChallengeId: challengeId, otpSentTo: sentTo });
  };

  useRegisterStepNav({
    onBack: goBack,
    onNext: verified ? handleNext : undefined,
    hideNext: !verified,
    nextLabel: t('nav.business') as string,
  });

  const validateRef = useRef(validate);
  validateRef.current = validate;
  const persistRef = useRef(persist);
  persistRef.current = persist;
  const verifiedRef = useRef(verified);
  verifiedRef.current = verified;

  useEffect(() => {
    registerStepGuard(() => {
      if (!verifiedRef.current) return false;
      if (!validateRef.current()) return false;
      persistRef.current();
      return true;
    });
    return () => registerStepGuard(null);
  }, [registerStepGuard]);

  const showOtherInput = local.deliveryCompanies.includes('other');
  const showStoreTypeOther = isOtherStoreType(local.storeType);

  if (!verified) {
    const masked =
      data.metadata.otpSentTo || maskPhone(data.businessInfo.phoneNumber);
    return (
      <StepCard
        title={t('otp.title') as string}
        subtitle={t('otp.subtitle', { phone: ltrIsolate(masked) }) as string}
      >
        <PhoneVerificationPanel
          phone={data.businessInfo.phoneNumber}
          language={data.metadata.language}
          challengeId={data.metadata.otpChallengeId ?? null}
          sentTo={data.metadata.otpSentTo ?? ''}
          onChallenge={handleChallenge}
          onVerified={handleVerified}
          onChangePhone={goBack}
        />
      </StepCard>
    );
  }

  return (
    <StepCard
      title={t('business.title')}
      subtitle={t('business.subtitle') as string}
    >
      <div className="flex flex-col gap-8 text-start">
        <FormSelect
          name="location"
          label={t('businessInfo.location')}
          options={cityOptions}
          placeholder={t('businessInfo.locationPlaceholder') as string}
          required
          value={local.location}
          onChange={(e) => setField('location', e.target.value)}
          error={errors.location}
        />

        <FormRadioGroup<BusinessCategory>
          name="businessCategory"
          label={t('businessCategory.question') as string}
          options={categoryOptions}
          value={local.businessCategory === '' ? null : local.businessCategory}
          onChange={(v) =>
            setLocal((s) => ({
              ...s,
              businessCategory: v,
              storeType: '',
              storeTypeOther: '',
            }))
          }
          error={errors.businessCategory}
          required
        />

        {local.businessCategory !== '' &&
          (storeTypeOptions.length > 0 ? (
            <FormSelect
              name="storeType"
              label={t('businessInfo.storeType')}
              options={storeTypeOptions}
              placeholder={t('businessInfo.storeTypePlaceholder') as string}
              required
              value={local.storeType}
              onChange={(e) =>
                setLocal((s) => ({
                  ...s,
                  storeType: e.target.value,
                  storeTypeOther: isOtherStoreType(e.target.value)
                    ? s.storeTypeOther
                    : '',
                }))
              }
              error={errors.storeType}
            />
          ) : (
            <p className="text-sm text-gray-500">
              {t('businessCategory.noStoreTypes')}
            </p>
          ))}

        {showStoreTypeOther && (
          <FormInput
            label={t('businessInfo.storeTypeOther') as string}
            placeholder={t('businessInfo.storeTypeOtherPlaceholder') as string}
            value={local.storeTypeOther}
            onChange={(e) => setField('storeTypeOther', e.target.value)}
            error={errors.storeTypeOther}
            required
          />
        )}

        <FormMultiSelect
          label={t('businessQuestions.sensitivities.label') as string}
          placeholder={t('businessQuestions.sensitivities.placeholder') as string}
          options={SENSITIVITIES.map((s) => ({
            value: s,
            label: t(`businessQuestions.sensitivities.options.${s}`) as string,
          }))}
          value={local.sensitivities}
          onChange={(next) =>
            setLocal((s) => ({ ...s, sensitivities: next as Sensitivity[] }))
          }
          error={errors.sensitivities}
        />

        <FormMultiSelect
          label={t('businessQuestions.kosherCertifications.label') as string}
          placeholder={
            t('businessQuestions.kosherCertifications.placeholder') as string
          }
          hint={t('businessQuestions.kosherCertifications.hint') as string}
          options={KOSHER_CERTIFICATIONS.map((cert) => ({
            value: cert,
            label: t(
              `businessQuestions.kosherCertifications.options.${cert}`,
            ) as string,
          }))}
          value={local.kosherCertifications}
          onChange={(next) =>
            setLocal((s) => ({
              ...s,
              kosherCertifications: next as KosherCertification[],
            }))
          }
          error={errors.kosherCertifications}
        />

        <FormMultiSelect
          label={t('businessQuestions.worksWithOtherDeliveryCompanies') as string}
          placeholder={
            t('businessQuestions.deliveryCompaniesPlaceholder') as string
          }
          hint={t('businessQuestions.deliveryCompaniesHint') as string}
          options={DELIVERY_COMPANIES.map((company) => ({
            value: company,
            label: t(
              `businessQuestions.deliveryCompanyOptions.${company}`,
            ) as string,
          }))}
          value={local.deliveryCompanies}
          onChange={setCompanies}
          error={errors.deliveryCompanies}
        />

        {showOtherInput && (
          <FormInput
            label={t('businessQuestions.otherDeliveryCompaniesLabel') as string}
            placeholder={
              t('businessQuestions.otherDeliveryCompaniesPlaceholder') as string
            }
            value={local.otherDeliveryCompanies}
            onChange={(e) => setField('otherDeliveryCompanies', e.target.value)}
            error={errors.otherDeliveryCompanies}
            required
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormInput
            label={t('businessInfo.businessPhoneNumber')}
            placeholder={
              t('businessInfo.businessPhoneNumberPlaceholder') as string
            }
            value={local.businessPhoneNumber}
            onChange={(e) => setField('businessPhoneNumber', e.target.value)}
            error={errors.businessPhoneNumber}
          />
          <FormInput
            label={t('businessInfo.email')}
            placeholder={t('businessInfo.emailPlaceholder') as string}
            type="email"
            required
            inputMode="email"
            value={local.email}
            onChange={(e) => setField('email', e.target.value)}
            error={errors.email}
          />
        </div>
      </div>
    </StepCard>
  );
};

export default Step3Business;
